import React, { useState, useEffect } from 'react';
import { Form, Button, TreeSelect } from 'antd';
import { useTranslation } from 'react-i18next';
import { FormTreeSelect } from 'components/form';
import { useClaimTypesProperties } from 'all-in-one-shared-api';
import { useProfileInfoContext } from 'contexts/profileInfo';
import { useFormContext } from 'react-hook-form';
import { useKeycloakReady } from 'hooks';
import { ClaimInfoCardUtils } from './ClaimInfoCard/utils';
import { ReceiptItem } from 'all-in-one-shared-api/dist/apis/claims/interfaces';
import MaskedInput from 'antd-mask-input';

const { TreeNode } = TreeSelect;

export const SUBNODE_PATTERN = /^__.{1,}/;

interface ReceiptItemModalProps {
  isModalVisible: boolean;
  handleModalClose: () => void;
  handleModalSubmit: (item: ReceiptItem) => void;
  editingItem?: ReceiptItem | null;
  isEditing: boolean;
}

const ReceiptItemModal: React.FC<ReceiptItemModalProps> = ({
  isModalVisible,
  handleModalClose,
  handleModalSubmit,
  editingItem,
  isEditing,
}) => {
  const { t, i18n } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { state: profileInfo } = useProfileInfoContext();

  const { watch } = useFormContext();
  const { token, ready } = useKeycloakReady();

  const patientMemberType = watch('patientMemberType');

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      // 如果用户选择了预设选项，使用预设的名称
      const selectedOption = claimTypesPropertiesData?.claimTypes?.find((option) => {
        if (option.benefits && option.benefits.length > 0) {
          return option.benefits.some((subOption) => subOption.code === values.code.replace('__', ''));
        } else return option.code === values.code;
      });
      const itemName =
        selectedOption?.benefits && selectedOption.benefits.length > 0
          ? selectedOption?.benefits.find((item) => item.code === values.code.replace('__', ''))
          : selectedOption;
      const item: ReceiptItem = {
        benefitCode: values.code,
        benefitDescription: itemName?.description ?? '',
        benefitChineseDescription: itemName?.chineseDescription ?? '',
        benefitAmount: parseFloat(values.amount).toString(),
        benefitSpecialty: itemName?.specialty ?? '',
      };
      handleModalSubmit(item);
      form.resetFields();
    } catch (error) {
      console.error('表单验证失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    handleModalClose();
  };

  const handleOnClaimTypeChange = (
    current: string,
    //  prev: string[]
  ) => {
    // if (!prev) {
    //   return [current];
    // }

    // if (current.match(SUBNODE_PATTERN)) {
    //   return [...prev.filter((o) => o.match(SUBNODE_PATTERN)), current];
    // }

    return [current];
  };

  const {
    shouldFetch: shouldFetchClaimTypesProperties,
    data: claimTypesPropertiesData,
    isLoading: isClaimTypesLoading,
  } = useClaimTypesProperties(
    {
      key: 'getClaimTypesProperties',
      params: {
        policyNumber: profileInfo?.policyNumber,
        memberId: profileInfo?.memberId,
        patientMemberType,
      },
    },
    token ?? '',
    profileInfo?.policyNumber ?? '',
    profileInfo?.memberId ?? '',
    patientMemberType,
  );
  useEffect(() => {
    shouldFetchClaimTypesProperties(ready && profileInfo !== undefined && patientMemberType !== undefined);
  }, [ready, patientMemberType, profileInfo, shouldFetchClaimTypesProperties]);

  useEffect(() => {
    if (isModalVisible) {
      if (isEditing && editingItem) {
        // 编辑模式：填充表单数据
        // 需要判断是否是benefits中的subcode，如果是则需要加上__前缀
        const isSubCode = claimTypesPropertiesData?.claimTypes?.some((option) =>
          option.benefits?.some((subOption) => subOption.code === editingItem.benefitCode),
        );

        form.setFieldsValue({
          code: isSubCode ? `__${editingItem.benefitCode}` : editingItem.benefitCode, // 如果是subcode需要加上__前缀
          amount: editingItem.benefitAmount,
        });
      } else {
        // 添加模式：重置表单
        form.resetFields();
      }
    }
  }, [isModalVisible, isEditing, editingItem, form, claimTypesPropertiesData]);

  return (
    <div style={{ display: isModalVisible ? 'block' : 'none' }}>
      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item
          name="code"
          label={t('newClaim.modal.benefitCode')}
          rules={[
            { required: true, message: t('common.error.requiredField') },
            () => ({
              validator(_, value) {
                console.log('value', value);
                if (value == 'OP') return Promise.reject(new Error(t('common.error.requiredField')));
                else return Promise.resolve();
              },
            }),
          ]}
        >
          {claimTypesPropertiesData?.claimTypes && (
            <FormTreeSelect
              name="code"
              treeDefaultExpandAll
              showSearch={false}
              placeholder={t('newClaim.start.selectClaimType')}
              handleOnChange={handleOnClaimTypeChange}
              loading={isClaimTypesLoading}
              // treeCheckable
            >
              {ClaimInfoCardUtils.formatClaimTypeData(claimTypesPropertiesData, i18n.language)?.map(
                ({ code, description, benefits }) => {
                  if ((benefits || []).length > 0) {
                    return (
                      <TreeNode
                        key={code}
                        value={code || ''}
                        title={description}
                        // disableCheckbox
                        // disabled
                        selectable={false}
                      >
                        {benefits?.map(({ code: subCode, description: subDescription }) => (
                          <TreeNode key={`__${subCode}` || ''} value={`__${subCode}` || ''} title={subDescription} />
                        ))}
                      </TreeNode>
                    );
                  }
                  return <TreeNode key={code} value={code || ''} title={description} />;
                },
              )}
            </FormTreeSelect>
          )}
        </Form.Item>

        <Form.Item
          name="amount"
          label={t('newClaim.modal.amount')}
          rules={[
            { required: true, message: t('common.error.requiredField') },
            { pattern: /^\d{1,8}(\.\d{0,2})?$/, message: t('newClaim.modal.amountFormat') },
            {
              validator: (_, value) => {
                if (value && parseFloat(value) === 0) {
                  return Promise.reject(new Error(t('newClaim.modal.amountFormat')));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          {/* <Input type="number" step="0.01" placeholder={t('newClaim.modal.amount')} addonAfter="$" /> */}
          <MaskedInput
            mask={[
              {
                mask: /^\d{1,8}$|(?=^.{1,11}$)^\d+\.\d{0,2}$/,
              },
            ]}
            placeholder={t('newClaim.modal.amountPlaceholder')}
            value={form.getFieldValue('amount')}
            onChange={(e) => {
              form.setFieldValue('amount', e.target.value);
            }}
            addonAfter="$"
          />
        </Form.Item>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <Button key="cancel" onClick={handleCancel}>
            {t('common.cancel')}
          </Button>
          <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>
            {t('life.lifeProfile.Save')}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default ReceiptItemModal;
